import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";
import _ from "lodash";
import { useQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import usePrevious from "src/util/usePrevious";
import { useAISidebar } from "src/hooks/useAISidebar";
import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import {
  getPublicAIChatTranscript,
  createNewAITranscriptVersion,
} from "../../../queries/ai.graphql";
import { POLLING_INTERVAL, SOURCE_TYPES, RESPONSE_TIMEOUT_MS } from "../consts";
import { AIChatContext } from "./AIChatContext";

export const AIChatProvider = ({ children, sourceId, subjectId }) => {
  const [question, setQuestion] = useState("");

  const [complete, setComplete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [error, setError] = useState(null);
  const [userHasScrolledUp, setUserHasScrolledUp] = useState(false);
  const [timeOfLastMessage, setTimeOfLastMessage] = useState();
  const [optimisticMessage, setOptimisticMessage] = useState();

  useEffect(() => {
    setTimeOfLastMessage(Date.now());
  }, []);

  /** @type {{ versionId: string|null, updateSidebarState: Function }} AI sidebar state and controls */
  const { versionId, updateSidebarState } = useAISidebar();

  /** @type {{ enableAIChat: boolean }} Feature flags from workspace context */
  const { enableAIChat } = useWorkspaceFeatureFlags();
  /**
   * Determines if AI chat is enabled based on feature flags and source
   * @type {boolean}
   * @memoized
   */
  const aiEnabled = useMemo(
    () => enableAIChat && sourceId === SOURCE_TYPES.chat_q_and_a,
    [enableAIChat, sourceId]
  );

  const { startPolling, stopPolling, loading, data, refetch, networkStatus } =
    useQuery(getPublicAIChatTranscript, {
      variables: { sourceId, params: JSON.stringify({ subjectId, versionId }) },
      fetchPolicy: "no-cache",
      skip: !aiEnabled || !subjectId || !sourceId,
      notifyOnNetworkStatusChange: true,
    });

  const [sendAiInvocationMutation] = useMutation(createNewAITranscriptVersion);

  const messages = useMemo(() => {
    return data?.result?.messages || [];
  }, [data]);

  const previousMessages = usePrevious(messages);

  /**
   * Helper function to clear messages and reset state
   * @returns {void}
   */
  const onSessionClear = useCallback(() => {
    // Reset current question and state
    setUserHasScrolledUp(false);
    setError(null);
    setQuestion("");
    setComplete(true);
  }, [setError]);

  /** @type {React.MutableRefObject<string|null>} Previous version ID for change detection */
  const previousVersionId = useRef(versionId);

  /**
   * Effect for managing chat session lifecycle based on versionId changes
   * @effect
   * @description Clears session when versionId changes from value to null,
   * sets loading state for new sessions, and tracks versionId changes
   * @param {Function} clearChatSession - Session clearing function
   * @param {string|null} versionId - Current version identifier
   * @param {number} messages.length - Number of current messages
   * @param {Function} setComplete - Completion state setter
   */
  useEffect(() => {
    // Clear session only when versionId changes from a value to null
    if (previousVersionId.current && !versionId) {
      onSessionClear();
    }

    previousVersionId.current = versionId;
  }, [onSessionClear, versionId, messages.length, setComplete]);

  const invokeAIResponse = useCallback(
    (newQuestion) => {
      /** @type {Object} Parameters for starting AI conversation */
      const params = {
        subjectId,
        question: newQuestion,
        versionId,
        sourceId,
      };

      setQuestion("");
      setIsSubmitting(true);
      setComplete(false);
      setTimeOfLastMessage(Date.now());
      setOptimisticMessage(newQuestion);
      sendAiInvocationMutation({
        variables: {
          sourceId,
          params:
            _.keys(params).length > 0 ? JSON.stringify(params) : undefined,
        },
      })
        .then(async (resp) => {
          /** @type {Object} Parsed message from AI response */
          let message = {};

          message = JSON.parse(resp?.data?.response?.message);

          if (message?.versionId) {
            updateSidebarState(true, message?.versionId);
          }
          if (resp?.data?.response?.startPolling) {
            startPolling(POLLING_INTERVAL);
            setIsPolling(true);
            await refetch();
            setOptimisticMessage();
          }

          setError(null);
        })
        .catch((err) => {
          stopPolling();
          setIsPolling(false);
          setComplete(true);
          setError(err);
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    },
    [
      sendAiInvocationMutation,
      sourceId,
      subjectId,
      versionId,
      updateSidebarState,
      startPolling,
      stopPolling,
      refetch,
    ]
  );

  const onSendQuestion = useCallback(
    () => invokeAIResponse(question),
    [invokeAIResponse, question]
  );

  const onRetry = useCallback(() => {
    invokeAIResponse("");
  }, [invokeAIResponse]);

  // This effect fires on every poll due to notifyOnNetworkStatusChange
  useEffect(() => {
    const shouldStop =
      timeOfLastMessage && Date.now() > timeOfLastMessage + 4500;

    const shouldTimeout =
      timeOfLastMessage && Date.now() > timeOfLastMessage + RESPONSE_TIMEOUT_MS;

    if (data?.result?.hasError && !error) {
      setError("Something went wrong.");
    } else if (!data?.result?.hasError && error) {
      setError(null);
    }

    if (shouldTimeout) {
      stopPolling();
      setIsPolling(false);
      setError("Timeout.");
    }

    if (!loading && !data?.result?.streaming && shouldStop) {
      stopPolling();
      setIsPolling(false);
    }

    if (data?.result?.streaming && !shouldTimeout) {
      startPolling(POLLING_INTERVAL);
      setIsPolling(true);
    }
  }, [
    networkStatus,
    data,
    loading,
    timeOfLastMessage,
    startPolling,
    stopPolling,
    error,
    isPolling,
  ]);

  const showLoader = useMemo(() => {
    return isSubmitting || isPolling;
  }, [isSubmitting, isPolling]);

  const values = useMemo(
    () => ({
      messages: optimisticMessage
        ? messages.concat([{ role: "user", content: optimisticMessage }])
        : messages,
      previousMessages,
      complete,
      hasError: !!error,
      setUserHasScrolledUp,
      userHasScrolledUp,
      question,
      setQuestion,
      onSessionClear,
      onSendQuestion,
      showLoader,
      onRetry,
      error,
    }),
    [
      optimisticMessage,
      messages,
      previousMessages,
      complete,
      setUserHasScrolledUp,
      userHasScrolledUp,
      question,
      setQuestion,
      onSessionClear,
      onSendQuestion,
      showLoader,
      onRetry,
      error,
    ]
  );

  if (!aiEnabled) {
    return null;
  }

  return (
    <AIChatContext.Provider value={values}>{children}</AIChatContext.Provider>
  );
};

AIChatProvider.propTypes = {
  children: PropTypes.node.isRequired,
  sourceId: PropTypes.string.isRequired,
  subjectId: PropTypes.string.isRequired,
};
